import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClipboardCheck, Loader2, AlertTriangle, Fingerprint } from "lucide-react";
import { toast } from "sonner";

const INSPECTORS = ["market_inspector", "inspector", "admin"];
const KIND = { lot: "#00FF7F", order: "#00FFFF", price_change: "#F59E0B", inspection: "#8A2BE2", anomaly: "#EF4444" };

const InspectionForm = ({ lots, onDone }) => {
  const [f, setF] = useState({ lot_id: lots[0]?.id || "", verdict: "pass", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/commons/inspections", f); toast.success(`Inspection ${data.verdict.toUpperCase()} anchored`); setF({ ...f, notes: "" }); onDone(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cls = "bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-[#8A2BE2] w-full";
  return (
    <form onSubmit={submit} className="qs-panel p-4 grid md:grid-cols-5 gap-2 items-end border-[#8A2BE2]/40" data-testid="inspection-form">
      <select data-testid="inspection-lot" className={`${cls} md:col-span-2`} value={f.lot_id} onChange={(e) => setF({ ...f, lot_id: e.target.value })}>
        {lots.map((l) => <option key={l.id} value={l.id}>{l.product} · {l.coop_name} · {l.status}</option>)}
      </select>
      <select data-testid="inspection-verdict" className={cls} value={f.verdict} onChange={(e) => setF({ ...f, verdict: e.target.value })}>
        {["pass", "fail", "recall"].map((v) => <option key={v}>{v}</option>)}
      </select>
      <input data-testid="inspection-notes" className={cls} placeholder="Findings (min 6 chars)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} required minLength={6} />
      <button data-testid="inspection-submit" disabled={busy} className="qs-cta bg-[#8A2BE2] text-white font-semibold px-3 py-1.5 text-xs flex items-center justify-center gap-1">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ClipboardCheck className="h-3.5 w-3.5" /> File inspection</>}
      </button>
    </form>
  );
};

export const AuditPanel = ({ audit, anomalies, lots, onChange }) => {
  const { user } = useAuth();
  return (
    <div className="space-y-4" data-testid="audit-panel">
      {user && INSPECTORS.includes(user.role) && lots.length > 0 && <InspectionForm lots={lots} onDone={onChange} />}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 qs-panel p-4" data-testid="audit-trail">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Audit trail · every event hash-anchored</div>
          <div className="space-y-2 max-h-[520px] overflow-auto">
            {audit.map((e) => (
              <div key={e.ref + e.kind} className="border-l-2 pl-3 py-0.5" style={{ borderColor: KIND[e.kind] }} data-testid={`audit-${e.kind}`}>
                <div className="flex justify-between text-[10px] font-mono text-slate-500"><span style={{ color: KIND[e.kind] }}>{e.kind}</span><span>{new Date(e.at).toLocaleString()}</span></div>
                <div className="text-sm text-slate-300">{e.summary}</div>
                <div className="text-[10px] font-mono text-slate-600 truncate flex items-center gap-1"><Fingerprint className="h-3 w-3" /> {e.anchor}</div>
              </div>
            ))}
            {!audit.length && <p className="text-sm text-slate-500">No events yet.</p>}
          </div>
        </div>
        <div className="lg:col-span-2 qs-panel p-4" data-testid="anomalies">
          <div className="text-[10px] uppercase tracking-widest text-[#EF4444] mb-3 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Auto-flagged anomalies · {anomalies.length}</div>
          <div className="space-y-2 max-h-[520px] overflow-auto">
            {anomalies.map((a) => (
              <div key={a.id} className={`border p-2 text-xs ${a.severity === "high" ? "border-[#EF4444]/60 bg-[#EF4444]/5" : "border-[#F59E0B]/40 bg-[#F59E0B]/5"}`} data-testid={`anomaly-${a.type}`}>
                <div className="flex justify-between font-mono"><span className={a.severity === "high" ? "text-[#EF4444]" : "text-[#F59E0B]"}>{a.type}</span><span className="text-slate-500">{a.city} · {a.product}</span></div>
                <div className="text-slate-300 mt-0.5">{a.detail}</div>
              </div>
            ))}
            {!anomalies.length && <p className="text-sm text-slate-500">Market within fair band — no anomalies.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
